import React, { useState, useRef, useEffect } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

interface ImagePair {
    left_id: number;
    right_id: number;
    leftImage: string;
    rightImage: string;
}


const MatchLetters: React.FC = () => {
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [totalQuestion, setTotalQuestion] = useState<any>([]);
    const [showImage, setShowImage] = useState(false);
    const [question, setQuestion] = useState('');
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [height, setHeight] = useState('0px');

    const modal = useRef<HTMLIonModalElement>(null);


    // Get element position relative to .match-container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect();
        setHeight((container.scrollHeight) + 'px');

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle left image click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget); console.log('left position - ', position)
        setSelectedLeft({ id, x: position.x, y: position.y });
    };

    // Handle right image click and draw SVG line
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedLeft) {
            const position = getRelativePosition(event.currentTarget); console.log('right position - ', position)

            // Check if the connection is correct
            const isCorrect = questions.some(pair => selectedLeft.id === rightId);

            setConnections([...connections, { x1: selectedLeft.x, y1: selectedLeft.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedLeft(null);
        }
    };

    // Reset connections
    const resetConnections = () => {
        setConnections([]);
        setSelectedLeft(null);
    };

    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            // Generate a random index
            const randomIndex = Math.floor(Math.random() * (i + 1));
            // Swap elements
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    // Fetch question data from an external source (replace with your actual API or JSON URL)
    const fetchQuestions = async () => {

        const alphabets = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
        const shuffledAlphabets = [...alphabets].sort(() => Math.random() - 0.5);

        const selectedAlphabets = shuffledAlphabets.slice(0, 4);
        const selectedLowercase = selectedAlphabets.map(letter => letter.toLowerCase());
        const shuffledSelectedLowercase = shuffleArray(selectedLowercase);

        const pairs = selectedAlphabets.map((letter, index) => {
            return {
                left_id: index + 1,
                leftImage: letter,
                rightImage: shuffledSelectedLowercase[index],
                right_id: selectedAlphabets.indexOf(shuffledSelectedLowercase[index].toUpperCase()) + 1
            };
        });

        setQuestions(pairs);
    };

    useEffect(() => {
        resetConnections()
        fetchQuestions();
        document.title = "Match the Letter – Nursery English Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const nextQuestion = () => {
        console.log('next question')
        resetConnections()
        setShowImage(true);
        setTimeout(() => {
            fetchQuestions();
            setShowImage(false);
        }, 3000);
    }



    return (
        <>
            <Helmet>
                <meta name="description" content='The "Match Letter" activity on Mamas Guidance is an interactive educational tool designed for nursery-level learners to enhance their letter recognition skills. This activity focuses on helping children identify and match uppercase and lowercase letters, a fundamental aspect of early literacy development.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Match the Letter</h1>

                                <p>
                                    Let your child explore the alphabet with our interactive <strong>Match the Letter</strong> worksheet. This activity supports letter recognition by helping kids match <strong>uppercase (capital)</strong> and <strong>lowercase (small)</strong> letters. It’s a simple and engaging way to build <strong>early literacy skills</strong> for nursery and preschool learners.
                                </p>

                                <h2>🔠 What’s in the Worksheet?</h2>
                                <p>
                                    On one side of the sheet, children see uppercase letters like A, B, C, etc., and on the other side, the corresponding lowercase letters. Their task is to <strong>draw lines connecting each capital letter to its matching small letter</strong>.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify and recognize A–Z letters</li>
                                    <li>Match uppercase to lowercase letters correctly</li>
                                    <li>Strengthen alphabet knowledge</li>
                                    <li>Improve fine motor skills with drawing/matching</li>
                                </ul>

                                <h2>👩‍🏫 How to Use the Worksheet</h2>
                                <ul>
                                    <li>Start by reviewing both capital and small letters with your child.</li>
                                    <li>Let them say each letter aloud while pointing to it.</li>
                                    <li>Draw lines to connect each matching pair.</li>
                                    <li>Use fun letter songs or flashcards for reinforcement.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/english-language/match-object-with-letter"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Match Object With Letter</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/english-language/object-from-letter"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Find Object from Letter</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/english-language/letter-from-object"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Find Letter from Object</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why Letter Matching is Important</h2>
                                <p>
                                    Recognizing both <strong>uppercase</strong> and <strong>lowercase</strong> letters is a critical part of reading readiness. By matching them, children learn that letters can have different forms but still represent the same sound and meaning.
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Match the Letter</h1>

                            <p>
                                Let your child explore the alphabet with our interactive <strong>Match the Letter</strong> worksheet. This activity supports letter recognition by helping kids match <strong>uppercase (capital)</strong> and <strong>lowercase (small)</strong> letters. It’s a simple and engaging way to build <strong>early literacy skills</strong> for nursery and preschool learners.
                            </p>

                            <h2>🔠 What’s in the Worksheet?</h2>
                            <p>
                                On one side of the sheet, children see uppercase letters like A, B, C, etc., and on the other side, the corresponding lowercase letters. Their task is to <strong>draw lines connecting each capital letter to its matching small letter</strong>.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify and recognize A–Z letters</li>
                                <li>Match uppercase to lowercase letters correctly</li>
                                <li>Strengthen alphabet knowledge</li>
                                <li>Improve fine motor skills with drawing/matching</li>
                            </ul>

                            <h2>👩‍🏫 How to Use the Worksheet</h2>
                            <ul>
                                <li>Start by reviewing both capital and small letters with your child.</li>
                                <li>Let them say each letter aloud while pointing to it.</li>
                                <li>Draw lines to connect each matching pair.</li>
                                <li>Use fun letter songs or flashcards for reinforcement.</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/nursery/english-language/match-object-with-letter"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Match Object With Letter</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/english-language/object-from-letter"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Find Object from Letter</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/english-language/letter-from-object"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Find Letter from Object</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why Letter Matching is Important</h2>
                            <p>
                                Recognizing both <strong>uppercase</strong> and <strong>lowercase</strong> letters is a critical part of reading readiness. By matching them, children learn that letters can have different forms but still represent the same sound and meaning.
                            </p>
                        </article>

                        <div ref={containerRef} className="contentPart additionWord matchImage match-container" style={{ position: "relative", height: "calc(-200px + 100vh)" }}>
                            {/* SVG for drawing lines */}
                            <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                                {connections.map((conn, index) => (
                                    <line
                                        key={index}
                                        x1={conn.x1}
                                        y1={conn.y1}
                                        x2={conn.x2}
                                        y2={conn.y2}
                                        stroke={conn.correct ? "green" : "red"} // Green if correct, Red if incorrect
                                        strokeWidth="3"
                                    />
                                ))}
                            </svg>


                            {/* <h6>Match the Capital Alphabet to its Small Alphabet</h6> */}
                            {/* Left & Right columns under SVG */}
                            <div className="mb-2 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%" }}>
                                <div className="left-column">
                                    {questions.map((item) => (
                                        <div
                                            key={item.left_id}
                                            className={`image-box ${selectedLeft?.id === item.left_id ? "selected" : ""}`}
                                            onClick={(e) => handleLeftClick(e, item.left_id)}
                                            style={{ cursor: "pointer", padding: "10px" }}
                                        >
                                            {item.leftImage}
                                        </div>
                                    ))}
                                </div>

                                <div className="right-column">
                                    {questions.map((item) => (
                                        <div key={item.left_id} className="image-box" onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                            {item.rightImage}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-2 mb-4 text-center" style={{ display: 'flex', gap: '10px' }}>
                                <div className="purpleButton" style={{ fontSize: '18px' }} onClick={resetConnections}>
                                    Reset
                                </div>
                                <div className="greenButton" style={{ fontSize: '18px' }} onClick={() => nextQuestion()}>Next &gt;&gt;</div>
                            </div>

                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default MatchLetters;
